import { useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeContext, type Theme } from '@/hooks/useTheme'

const STORAGE_KEY = 'opconsole.theme'

function readInitialTheme(): Theme {
    if (typeof window === 'undefined') return 'light'
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(readInitialTheme)

    useEffect(() => {
        const root = document.documentElement
        if (theme === 'dark') root.classList.add('dark')
        else root.classList.remove('dark')
        root.style.colorScheme = theme
        window.localStorage.setItem(STORAGE_KEY, theme)
    }, [theme])

    const setTheme = useCallback((t: Theme) => setThemeState(t), [])
    const toggle = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), [])

    const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle])

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
